<template>
  <div class="pl-login">

    <!-- ===================== BRAND PANEL ===================== -->
    <div class="pl-brand pl-base-bg">
      <div class="pl-glow pl-glow--a"></div>
      <div class="pl-glow pl-glow--b"></div>
      <div class="pl-glow pl-glow--c"></div>

      <svg class="pl-grid" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid-verify" width="52" height="52" patternUnits="userSpaceOnUse">
            <path d="M 52 0 L 0 0 0 52" fill="none" stroke="rgba(255,255,255,0.22)" stroke-width="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-verify)" />
      </svg>

      <div class="pl-brand-content">
        <div class="pl-icon-tile">
          <div class="pl-icon-clip">
            <img src="@/assets/ProfitLedgex.png" alt="ProfitLedgex" class="pl-icon-img" />
          </div>
        </div>
        <h1 class="pl-wordmark">ProfitLedgex</h1>
        <p class="pl-tagline">Intelligent business insights with seamless invoicing.</p>
        <div class="pl-accent-bar"></div>
      </div>
    </div>

    <!-- ===================== FORM PANEL ===================== -->
    <div class="pl-form-panel">
      <div class="pl-form-glow"></div>
      <div class="pl-form-glow-2"></div>

      <div class="pl-form">
        <p class="pl-eyebrow">One last step</p>
        <h2 class="pl-heading">Verify your email</h2>
        <p class="pl-sub">
          Enter the 6-digit code sent to
          <strong v-if="email">{{ email }}</strong>
          <span v-else>your email</span>.
        </p>

        <v-form ref="otpForm" v-model="isFormValid" lazy-validation @submit.prevent="handleVerify">
          <label class="pl-label">Verification code</label>
          <v-text-field
            v-model="otp"
            placeholder="123456"
            inputmode="numeric"
            maxlength="6"
            outlined
            hide-details="auto"
            :rules="otpRules"
            class="pl-field pl-otp-field"
            color="#0a6cc0" />

          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">
            {{ errorMessage }}
          </v-alert>

          <v-alert v-if="infoMessage" type="success" dense outlined class="mt-3 mb-0">
            {{ infoMessage }}
          </v-alert>

          <v-btn
            type="submit"
            block
            depressed
            height="54"
            :loading="loading"
            :disabled="!isFormValid || loading"
            class="pl-submit mt-5">
            Verify &amp; continue
          </v-btn>
        </v-form>

        <p class="pl-switch">
          Didn't get the code?
          <a
            class="pl-switch-link"
            :class="{ 'pl-switch-link--disabled': resendCooldown > 0 || resending }"
            @click="handleResend">
            {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code' }}
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VerifyOtpPage',

  data: () => ({
    userId: '',
    email: '',
    otp: '',
    isFormValid: false,
    loading: false,
    resending: false,
    resendCooldown: 0,
    cooldownTimer: null,
    errorMessage: '',
    infoMessage: '',

    otpRules: [
      (v) => !!v || 'Verification code is required',
      (v) => /^\d{6}$/.test(v) || 'Enter the 6-digit code',
    ],
  }),

  created() {
    this.userId = this.$route.query.userId || '';
    this.email = this.$route.query.email || '';

    if (!this.userId) {
      this.$router.replace('/signup');
      return;
    }

    this.startCooldown(60);
  },

  beforeDestroy() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  },

  methods: {
    startCooldown(seconds) {
      this.resendCooldown = seconds;
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.cooldownTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }, 1000);
    },

    async handleVerify() {
      this.errorMessage = '';
      this.infoMessage = '';
      if (!this.$refs.otpForm.validate()) return;
      this.loading = true;
      try {
        await this.$store.dispatch('auth/verifyOtp', {
          userId: this.userId,
          otp: this.otp,
        });
        this.$toast.success('Account created successfully!');
        this.$router.push('/dashboard');
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Verification failed';
      } finally {
        this.loading = false;
      }
    },

    async handleResend() {
      if (this.resendCooldown > 0 || this.resending) return;
      this.errorMessage = '';
      this.infoMessage = '';
      this.resending = true;
      try {
        await this.$store.dispatch('auth/resendOtp', { userId: this.userId });
        this.infoMessage = 'A new code has been sent to your email.';
        this.startCooldown(60);
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to resend code';
      } finally {
        this.resending = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Layout (shared with login.vue's brand-panel visual system) ── */
.pl-login {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  font-family: 'Manrope', system-ui, sans-serif;
  color: #0e2433;
}

.pl-brand {
  position: relative;
  flex: 0 0 54%;
  overflow: hidden;
}

.pl-glow { position: absolute; border-radius: 50%; pointer-events: none; }

.pl-glow--a {
  top: -90px; left: -90px;
  width: 440px; height: 440px;
  background: radial-gradient(circle,
    rgba(190,235,255,0.95) 0%,
    rgba(110,195,255,0.60) 32%,
    rgba(60,140,255,0.18) 58%,
    transparent 72%);
  filter: blur(38px);
  animation: pl-floatA 18s ease-in-out infinite;
}
.pl-glow--b {
  bottom: -160px; right: -80px;
  width: 480px; height: 480px;
  background: radial-gradient(circle, rgba(50,220,130,0.28), transparent 68%);
  filter: blur(90px);
  animation: pl-floatB 22s ease-in-out infinite;
}
.pl-glow--c {
  top: 38%; right: 12%;
  width: 220px; height: 220px;
  background: radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%);
  filter: blur(80px);
  animation: pl-floatC 14s ease-in-out infinite;
}

.pl-grid {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  z-index: 1; pointer-events: none;
}

.pl-brand-content {
  position: absolute; inset: 0; z-index: 3;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 48px; text-align: center;
  animation: pl-rise 0.8s ease-out both;
}

.pl-icon-tile {
  padding: 16px;
  border-radius: 36px;
  background: rgba(255,255,255,0.12);
  box-shadow:
    0 28px 64px rgba(2,20,40,0.50),
    inset 0 0 0 1.5px rgba(255,255,255,0.22),
    inset 0 2px 4px rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.pl-icon-clip {
  width: 128px;
  height: 128px;
  overflow: hidden;
  border-radius: 26px;
  box-shadow: 0 8px 24px rgba(2,20,40,0.40);
  position: relative;
}
.pl-icon-img {
  display: block;
  position: absolute;
  top: 0; left: 0;
  width: 128px;
  height: auto;
}

.pl-wordmark {
  margin: 28px 0 0;
  font-weight: 800;
  font-size: 46px;
  letter-spacing: -0.025em;
  color: #fff;
  text-shadow:
    0 2px 12px rgba(2,20,40,0.35),
    0 0 60px rgba(120,210,255,0.25);
}

.pl-tagline {
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 500;
  color: rgba(225,245,255,0.88);
  letter-spacing: 0.01em;
}

.pl-accent-bar {
  margin-top: 28px;
  height: 3px; width: 56px;
  border-radius: 3px;
  background: linear-gradient(90deg, #7fd4ff, #74f0a8);
  box-shadow: 0 0 12px rgba(127,212,255,0.6);
}

/* ════════════════════════════
   RIGHT — Form Panel
════════════════════════════ */
.pl-form-panel {
  position: relative;
  flex: 1 1 46%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 48px;
  background: #f4f6f9;
  overflow-y: auto;
}

.pl-form-glow {
  position: absolute; top: -60px; right: -60px;
  width: 340px; height: 340px;
  background: radial-gradient(circle at 60% 40%, rgba(15,138,106,0.09), transparent 68%);
  pointer-events: none;
}
.pl-form-glow-2 {
  position: absolute; bottom: -80px; left: -40px;
  width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(10,108,192,0.07), transparent 68%);
  pointer-events: none;
}

.pl-form {
  position: relative;
  width: 100%;
  max-width: 400px;
  animation: pl-rise 0.8s ease-out 0.08s both;
}

.pl-eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #0a8a6a;
}

.pl-heading {
  margin: 10px 0 6px;
  font-weight: 800;
  font-size: 30px;
  letter-spacing: -0.025em;
  color: #0b1e2d;
  line-height: 1.2;
}

.pl-sub {
  margin: 0 0 26px;
  font-size: 14.5px;
  color: #64747f;
  font-weight: 400;
}

.pl-label {
  display: block;
  margin: 16px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374955;
  letter-spacing: 0.01em;
}

.pl-field >>> .v-input__slot {
  border-radius: 12px !important;
  background: #ffffff !important;
  min-height: 50px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
}
.pl-field >>> fieldset {
  border-color: #cdd5dc !important;
  border-width: 1.5px !important;
}
.pl-field >>> .v-input--is-focused fieldset {
  border-color: #0a6cc0 !important;
  border-width: 2px !important;
}
.pl-field >>> input {
  font-size: 14.5px;
  color: #0b1e2d;
}
.pl-field >>> input::placeholder { color: #a8b5bf; }

.pl-otp-field >>> input {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 10px;
  text-align: center;
}

.pl-submit {
  border-radius: 12px !important;
  background: linear-gradient(135deg, #0a5fb5 0%, #0d8f75 52%, #16b358 100%) !important;
  color: #fff !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em !important;
  font-size: 16px !important;
  text-transform: none !important;
  box-shadow:
    0 8px 24px rgba(10,108,192,0.30),
    0 4px 8px rgba(13,143,117,0.20) !important;
  transition: opacity 0.2s, box-shadow 0.2s !important;
}
.pl-submit:hover {
  box-shadow:
    0 12px 32px rgba(10,108,192,0.38),
    0 6px 12px rgba(13,143,117,0.28) !important;
}

.pl-switch {
  margin-top: 22px;
  text-align: center;
  font-size: 13.5px;
  color: #64747f;
}

.pl-switch-link {
  color: #0a6cc0;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}
.pl-switch-link:hover {
  text-decoration: underline;
}
.pl-switch-link--disabled {
  color: #a8b5bf;
  cursor: default;
  pointer-events: none;
}

/* ── Animations ── */
@keyframes pl-floatA {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(30px, -42px); }
}
@keyframes pl-floatB {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(-28px, 36px); }
}
@keyframes pl-floatC {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(18px, 26px); }
}
@keyframes pl-rise {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Mobile ── */
@media (max-width: 860px) {
  .pl-login {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .pl-brand {
    flex: 0 0 auto;
    height: 220px;
    min-height: unset;
  }

  .pl-brand-content {
    padding: 24px 20px;
  }

  .pl-icon-tile {
    padding: 10px;
    border-radius: 22px;
  }

  .pl-icon-clip {
    width: 76px;
    height: 76px;
    border-radius: 16px;
  }

  /* Same uncropped fill as desktop — see login.vue for why this changed
     from a hand-tuned crop to a plain scale-to-fit. */
  .pl-icon-img {
    width: 76px;
    height: auto;
    top: 0;
    left: 0;
  }

  .pl-wordmark {
    font-size: 26px;
    margin: 12px 0 0;
  }

  .pl-tagline {
    font-size: 13px;
    margin: 6px 0 0;
    white-space: normal;
  }

  .pl-accent-bar {
    margin-top: 12px;
    width: 36px;
  }

  .pl-glow--a {
    width: 240px; height: 240px;
    top: -50px; left: -50px;
    filter: blur(28px);
  }

  .pl-form-panel {
    flex: 1;
    min-height: unset;
    align-items: flex-start;
    padding: 36px 24px 52px;
  }

  .pl-form { max-width: 100%; }

  .pl-heading { font-size: 24px; }

  .pl-sub {
    font-size: 14px;
    margin-bottom: 18px;
  }
}
</style>
